import { v4 as uuidv4 } from 'uuid';
import { differenceInMilliseconds } from 'date-fns';
import { captureEvent } from '@sentry/browser';

const SESSION_TIMEOUT = 30 * 60000;
const SESSION_GUID_KEY = 'autolist-web-events-sessionGuid';
const SESSION_TOUCHED_KEY = 'autolist-web-events-sessionTouched';
const CLIENT_FIRST_TOUCHED_TIMESTAMP_KEY = 'autolist-web-event-clientFirstTouchedTimestamp';
const SEQUENCE_NUMBER_KEY_NAMESPACE = 'autolist-web-events-sequenceNumber';
const CORE_SEQUENCE_NUMBER_KEY_NAMESPACE = 'events-sequenceNumber';
export const CLIENT_GUID_KEY = 'events-clientGuid';

function clearSessionGuid(): void {
  window.localStorage.removeItem(SESSION_GUID_KEY);
}

export function getClientGuid(): string {
  let clientGuid = window.localStorage.getItem(CLIENT_GUID_KEY);

  if (clientGuid) {
    return clientGuid;
  }

  clientGuid = uuidv4();
  setLocalStorageItem(CLIENT_GUID_KEY, clientGuid);

  return clientGuid;
}

export function getSessionGuid(): string {
  let sessionGuid = localStorage.getItem(SESSION_GUID_KEY);
  if (!sessionGuid) {
    sessionGuid = uuidv4();
    setLocalStorageItem(SESSION_GUID_KEY, sessionGuid);
  }
  return sessionGuid;
}

export function getClientFirstTouchedTimestamp(): number {
  const clientFirstTouchedTimestamp = window.localStorage.getItem(
    CLIENT_FIRST_TOUCHED_TIMESTAMP_KEY
  );

  if (clientFirstTouchedTimestamp) {
    return parseInt(clientFirstTouchedTimestamp, 10);
  }

  const newClientFirstTouchedTimestamp = new Date().getTime();
  setLocalStorageItem(CLIENT_FIRST_TOUCHED_TIMESTAMP_KEY, String(newClientFirstTouchedTimestamp));

  return newClientFirstTouchedTimestamp;
}

export function getSequenceNumber(): number {
  const sequenceNumberKey = `${SEQUENCE_NUMBER_KEY_NAMESPACE}/${getSessionGuid()}`;
  let sequenceNumber = Number(window.localStorage.getItem(sequenceNumberKey));
  if (!sequenceNumber) {
    sequenceNumber = 0;
    setLocalStorageItem(sequenceNumberKey, String(sequenceNumber));
  }
  return sequenceNumber;
}

export function incrementSequenceNumber(): void {
  const sequenceNumberKey = `${SEQUENCE_NUMBER_KEY_NAMESPACE}/${getSessionGuid()}`;
  const sequenceNumber = getSequenceNumber() + 1;
  setLocalStorageItem(sequenceNumberKey, String(sequenceNumber));
}

// mark the session as having started. if we have never started a session before, or we
// have reached the session timeout, then clear the session guid and return true.
// otherwise returns false
export function touchSession(): boolean {
  const currentTimeStamp = new Date().toISOString();
  const lastSessionTouchedTimeStamp = window.localStorage.getItem(SESSION_TOUCHED_KEY);
  setLocalStorageItem(SESSION_TOUCHED_KEY, currentTimeStamp);
  if (lastSessionTouchedTimeStamp) {
    const millisElapsed = differenceInMilliseconds(
      new Date(currentTimeStamp),
      new Date(lastSessionTouchedTimeStamp)
    );
    if (millisElapsed < SESSION_TIMEOUT) {
      return false;
    }
  }
  clearSessionData();
  return true;
}

export function clearSessionData(): void {
  clearSessionGuid();
  clearAutolistWebSequenceNumberItems();
}

function clearAutolistWebSequenceNumberItems(): void {
  Object.keys(localStorage).forEach((key) => {
    if (key.includes(SEQUENCE_NUMBER_KEY_NAMESPACE)) {
      localStorage.removeItem(key);
    }
  });
}

export function clearAutolistCoreSequenceNumberItems(): void {
  Object.keys(localStorage).forEach((key) => {
    if (key.includes(CORE_SEQUENCE_NUMBER_KEY_NAMESPACE)) {
      localStorage.removeItem(key);
    }
  });
}

export interface LocalStorageSpaceAudit {
  webSequenceNumberItemsCount: number;
  coreSequenceNumberItemsCount: number;
  localStorageItemSizes: { [key: string]: number };
  totalUsage: number;
}

export function getLocalStorageSpaceAudit(): LocalStorageSpaceAudit {
  let coreSequenceNumberItemsCount = 0;
  let webSequenceNumberItemsCount = 0;
  const localStorageItemSizes: { [key: string]: number } = {};

  Object.entries(localStorage).forEach(([key, value]) => {
    if (key.includes(SEQUENCE_NUMBER_KEY_NAMESPACE)) {
      webSequenceNumberItemsCount += 1;
    } else if (key.includes(CORE_SEQUENCE_NUMBER_KEY_NAMESPACE)) {
      coreSequenceNumberItemsCount += 1;
    } else {
      localStorageItemSizes[key] = new Blob([key]).size + new Blob([value]).size;
    }
  });

  const totalUsage = new Blob(Object.values(localStorage)).size;

  return {
    webSequenceNumberItemsCount,
    coreSequenceNumberItemsCount,
    localStorageItemSizes,
    totalUsage,
  };
}

export function setLocalStorageItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    const auditData = getLocalStorageSpaceAudit();
    captureEvent({
      message: 'Auditing local storage space usage',
      extra: {
        auditData,
        errorMessage: error.message,
        errorCode: error.code,
        error,
      },
    });
    clearSessionData();
    clearAutolistCoreSequenceNumberItems();
    throw error;
  }
}
